import {
  Home,
  FileText,
  BookMarked,
  Scroll,
  Volume2,
  Library,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  Users,
  Database,
  Settings,
  ExternalLink,
  Star,
  X,
} from "lucide-react";
import { useState, useLayoutEffect, type MouseEvent } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { getAdminRefreshToken, clearLocalStorage } from "@lib/storeManager";
import { adminAPI } from "@/project/jain_elibrary-admin/services/adminService";
import { paths } from "@/project/jain_elibrary-admin/utils/paths";
import {
  ADMIN_SIDEBAR_SCROLL_ATTR,
  captureAdminNavScroll,
} from "./adminNavScroll";

const getSidebarItems = () => [
  { icon: Home, label: "Home", path: paths.dashboard },
  {
    icon: Settings,
    label: "Change Password",
    path: paths.changePassword,
  },
  { icon: Users, label: "User Profile", path: paths.profile },
  { icon: ExternalLink, label: "Logout", path: "#" },
];

const getUsersRelatedItems = () => [
  {
    label: "Update Users Download Count",
    path: paths.users.updateDownloadCount,
  },
  {
    label: "User Registration",
    path: paths.users.registration,
  },
  {
    label: "User Search History",
    path: paths.users.searchHistory,
  },
  {
    label: "User Site Visits",
    path: paths.users.siteVisits,
  },
  { label: "User Download", path: paths.users.download },
  { label: "Export Users", path: paths.users.export },
  { label: "Merge Data", path: paths.users.mergeData },
  { label: "Admins", path: paths.users.admins },
];

const getDataRecordItems = () => [
  {
    icon: Library,
    label: "Export E-Library Data",
    path: paths.export.elibrary,
  },
  {
    icon: FileText,
    label: "Export Book Data",
    path: paths.export.book,
  },
  {
    icon: FileText,
    label: "Export Article Data",
    path: paths.export.article,
  },
  {
    icon: BookMarked,
    label: "Export Magazine Data",
    path: paths.export.magazine,
  },
  {
    icon: FileText,
    label: "Export Agam Data",
    path: paths.export.agam,
  },
  {
    icon: Scroll,
    label: "Export Manuscript Data",
    path: paths.export.manuscript,
  },
  {
    icon: Volume2,
    label: "Export Audio Data",
    path: paths.export.audio,
  },
];

const getShoppingCartItems = () => [
  {
    label: "Manage Stock India",
    path: paths.shoppingCart.manageStockIndia,
  },
  {
    label: "Manage Stock USA",
    path: paths.shoppingCart.manageStockUSA,
  },
  {
    label: "Manage Stock Special USA",
    path: paths.shoppingCart.manageStockSpecialUSA,
  },
  {
    label: "Manage Stock Other Country",
    path: paths.shoppingCart.manageStockOther,
  },
  {
    label: "Ordering addresses",
    path: paths.shoppingCart.manageAddress,
  },
  {
    label: "Order Management",
    path: paths.shoppingCart.orderManagement,
  },
  {
    label: "Order Email Templates",
    path: paths.shoppingCart.orderEmailTemplates,
  },
  {
    label: "Purchase orders",
    path: paths.shoppingCart.purchaseOrders,
  },
  {
    label: "Shipping Management",
    path: paths.shoppingCart.shippingManagement,
  },
];

const getHomepageItems = () => [
  { label: "Featured Books", path: paths.content.featuredBooks },
  { label: "Home Page Banner", path: paths.content.banner },
  { label: "Home page hero", path: paths.content.homeHero },
  { label: "Footer (copyright)", path: paths.content.footer },
  { label: "About Us (site)", path: paths.content.aboutUs },
  { label: "Committee (site)", path: paths.content.committee },
  { label: "Contact Us (site)", path: paths.content.contact },
  { label: "Donate page (site)", path: paths.content.donate },
  { label: "Donate options (cards)", path: paths.content.donateOptions },
  { label: "Terms (site)", path: paths.content.terms },
  { label: "Privacy Policy (site)", path: paths.content.privacyPolicy },
];

function pathnameMatchesRoute(pathname: string, routePath: string) {
  if (!routePath || routePath === "#") {
    return false;
  }
  return pathname === routePath || pathname.startsWith(`${routePath}/`);
}

function pathnameMatchesAny(
  pathname: string,
  items: { path: string }[]
): boolean {
  return items.some(item => pathnameMatchesRoute(pathname, item.path));
}

function routeIsActive(pathname: string, to: string, end?: boolean) {
  if (end) {
    return pathname === to;
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Client-side navigation with preventDefault so the browser does not scroll the page.
 * Also passes preventScrollReset to navigate for data-router compatibility.
 */
function SidebarNavLink({
  to,
  className,
  activeClassName,
  end,
  children,
  onAfterNavigate,
}: {
  to: string;
  className?: string;
  activeClassName?: string;
  end?: boolean;
  children: React.ReactNode;
  onAfterNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = routeIsActive(location.pathname, to, end);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    captureAdminNavScroll();
    navigate(to, { preventScrollReset: true });
    onAfterNavigate?.();
  };

  return (
    <a
      href={to}
      className={cn(
        className,
        isActive && activeClassName,
        "outline-none focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-admin-sidebar"
      )}
      onClick={handleClick}
      aria-current={isActive ? "page" : undefined}
    >
      {children}
    </a>
  );
}

export function AdminSidebar({
  mobileOpen = false,
  onMobileClose,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const [usersRelatedOpen, setUsersRelatedOpen] = useState(false);
  const [dataRecordsOpen, setDataRecordsOpen] = useState(false);
  const [shoppingCartOpen, setShoppingCartOpen] = useState(false);
  const [homepageOpen, setHomepageOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useLayoutEffect(() => {
    const pathname = location.pathname;
    flushSync(() => {
      if (pathnameMatchesAny(pathname, getUsersRelatedItems())) {
        setUsersRelatedOpen(true);
      }
      if (pathnameMatchesAny(pathname, getDataRecordItems())) {
        setDataRecordsOpen(true);
      }
      if (pathnameMatchesAny(pathname, getShoppingCartItems())) {
        setShoppingCartOpen(true);
      }
      if (pathnameMatchesAny(pathname, getHomepageItems())) {
        setHomepageOpen(true);
      }
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    onMobileClose?.();
    try {
      const refreshToken = getAdminRefreshToken();
      if (refreshToken) {
        try {
          await adminAPI.post("/auth/logout/", { refresh: refreshToken });
        } catch (error) {
          console.error("Logout API error:", error);
        }
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      clearLocalStorage();
      navigate(paths.login);
    }
  };

  return (
    <aside
      {...{ [ADMIN_SIDEBAR_SCROLL_ATTR]: "" }}
      className={cn(
        "fixed left-0 top-0 z-50 h-screen w-64 max-w-[min(100vw-2rem,20rem)] overflow-y-auto bg-admin-sidebar text-white [overflow-anchor:none] [&_a]:scroll-m-0",
        "transition-transform duration-200 ease-out will-change-transform",
        "-translate-x-full lg:translate-x-0",
        mobileOpen && "translate-x-0"
      )}
    >
      <div className="border-b border-white/10 p-4 sm:p-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-admin-green">
              <Library className="h-6 w-6" />
            </div>
            <span className="truncate text-base font-bold sm:text-lg">
              JAIN eLibrary
            </span>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg p-2 text-white/80 transition-colors hover:bg-admin-sidebar-hover hover:text-white lg:hidden"
            aria-label="Close menu"
            onClick={onMobileClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <nav className="space-y-4 p-3 sm:p-4">
        <div>
          <p className="px-4 pb-2 text-xs uppercase tracking-wide text-white/50">
            My Desk
          </p>
          <div className="space-y-1">
            {getSidebarItems().map(item => {
              if (item.label === "Logout") {
                return (
                  <button
                    key={item.label}
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-white/70 transition-colors hover:bg-admin-sidebar-hover hover:text-white"
                  >
                    <item.icon className="h-5 w-5" />
                    <span className="text-sm">{item.label}</span>
                  </button>
                );
              }
              return (
                <SidebarNavLink
                  key={item.label}
                  to={item.path}
                  end={item.path === paths.home || item.path === paths.login}
                  activeClassName="bg-admin-sidebar-hover text-white"
                  className="flex items-center gap-3 rounded-lg px-4 py-3 text-white/70 transition-colors hover:bg-admin-sidebar-hover hover:text-white"
                  onAfterNavigate={onMobileClose}
                >
                  <item.icon className="h-5 w-5" />
                  <span className="text-sm">{item.label}</span>
                </SidebarNavLink>
              );
            })}
          </div>
        </div>

        <div>
          <p className="px-4 pb-2 text-xs uppercase tracking-wide text-white/50">
            Management
          </p>
          <button
            type="button"
            onClick={() => setUsersRelatedOpen(!usersRelatedOpen)}
            aria-expanded={usersRelatedOpen}
            className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-white/70 outline-none transition-colors hover:bg-admin-sidebar-hover hover:text-white focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-admin-sidebar"
          >
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5" />
              <span className="text-sm">Users Related</span>
            </div>
            {usersRelatedOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {usersRelatedOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {getUsersRelatedItems().map(item => (
                <SidebarNavLink
                  key={item.label}
                  to={item.path}
                  className="block rounded-lg px-4 py-2 text-sm text-white/60 transition-colors hover:text-white"
                  activeClassName="bg-admin-sidebar-hover text-white"
                  onAfterNavigate={onMobileClose}
                >
                  {item.label}
                </SidebarNavLink>
              ))}
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setDataRecordsOpen(!dataRecordsOpen)}
            aria-expanded={dataRecordsOpen}
            className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-white/70 outline-none transition-colors hover:bg-admin-sidebar-hover hover:text-white focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-admin-sidebar"
          >
            <div className="flex items-center gap-3">
              <Database className="h-5 w-5" />
              <span className="text-sm">Data Records</span>
            </div>
            {dataRecordsOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {dataRecordsOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {getDataRecordItems().map(item => (
                <SidebarNavLink
                  key={item.label}
                  to={item.path}
                  className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm text-white/60 transition-colors hover:text-white"
                  activeClassName="bg-admin-sidebar-hover text-white"
                  onAfterNavigate={onMobileClose}
                >
                  {item.icon && <item.icon className="h-4 w-4" />}
                  <span>{item.label}</span>
                </SidebarNavLink>
              ))}
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setShoppingCartOpen(!shoppingCartOpen)}
            aria-expanded={shoppingCartOpen}
            className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-white/70 outline-none transition-colors hover:bg-admin-sidebar-hover hover:text-white focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-admin-sidebar"
          >
            <div className="flex items-center gap-3">
              <ShoppingCart className="h-5 w-5" />
              <span className="text-sm">Shopping Cart</span>
            </div>
            {shoppingCartOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {shoppingCartOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {getShoppingCartItems().map(item => (
                <SidebarNavLink
                  key={item.path}
                  to={item.path}
                  className="block rounded-lg px-4 py-2 text-sm text-white/60 transition-colors hover:text-white"
                  activeClassName="bg-admin-sidebar-hover text-white"
                  onAfterNavigate={onMobileClose}
                >
                  {item.label}
                </SidebarNavLink>
              ))}
            </div>
          )}
        </div>

        <div>
          <button
            type="button"
            onClick={() => setHomepageOpen(!homepageOpen)}
            aria-expanded={homepageOpen}
            className="flex w-full items-center justify-between rounded-lg px-4 py-3 text-white/70 outline-none transition-colors hover:bg-admin-sidebar-hover hover:text-white focus-visible:ring-2 focus-visible:ring-white/45 focus-visible:ring-offset-2 focus-visible:ring-offset-admin-sidebar"
          >
            <div className="flex items-center gap-3">
              <Star className="h-5 w-5" />
              <span className="text-sm">Homepage</span>
            </div>
            {homepageOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {homepageOpen && (
            <div className="ml-8 mt-1 space-y-1">
              {getHomepageItems().map(item => (
                <SidebarNavLink
                  key={item.path}
                  to={item.path}
                  className="block rounded-lg px-4 py-2 text-sm text-white/60 transition-colors hover:text-white"
                  activeClassName="bg-admin-sidebar-hover text-white"
                  onAfterNavigate={onMobileClose}
                >
                  {item.label}
                </SidebarNavLink>
              ))}
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
}
